import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Seo } from '@/components/Seo'
import { api, ApiError } from '@/lib/api'
import { useCartStore } from '@/lib/cart'
import { formatPrice } from '@/lib/money'

interface FormState {
  customerName: string
  customerEmail: string
  customerPhone: string
  shippingAddress: string
  note: string
}

const EMPTY: FormState = {
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  shippingAddress: '',
  note: '',
}

export default function Checkout() {
  const navigate = useNavigate()
  const items = useCartStore((s) => s.items)
  const total = useCartStore((s) => s.getTotalPrice())
  const clearCart = useCartStore((s) => s.clearCart)

  const [form, setForm] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (items.length === 0) return
    setSubmitting(true)
    try {
      await api.orders.create({
        ...form,
        items: items.map((item) => ({ productId: String(item.id), quantity: item.quantity })),
      })
      clearCart()
      toast.success('Đã nhận đơn hàng. LumiSkin sẽ gọi xác nhận trong hôm nay.')
      navigate('/')
    } catch (err) {
      // Lỗi từ server đã có câu chữ cho người dùng; chỉ khi không có mới dùng câu chung.
      const message = err instanceof ApiError ? err.message : 'Không gửi được đơn hàng. Bạn thử lại giúp nhé.'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  if (items.length === 0) {
    return (
      <>
        <Seo title="Thanh toán — LumiSkin" />
        <div data-upbo-src="src/pages/Checkout.tsx:62" className="mx-auto w-full max-w-6xl px-6 py-24 text-center">
          <p data-upbo-src="src/pages/Checkout.tsx:63" className="text-[#9c7a72]">Chưa có sản phẩm nào để thanh toán.</p>
          <Link to="/cua-hang" className="mt-4 inline-block text-sm text-[#7a4f45] underline">
            Đến cửa hàng
          </Link>
        </div>
      </>
    )
  }

  const field =
    'w-full rounded-xl border border-[#e0c8c1] bg-[#fdf8f6] px-4 py-3 text-sm text-[#5f3f38] outline-none transition-colors placeholder:text-[#c0a49d] focus:border-[#c98b7e]'

  return (
    <>
      <Seo title="Thanh toán — LumiSkin" />

      <div data-upbo-src="src/pages/Checkout.tsx:79" className="mx-auto grid w-full max-w-5xl gap-12 px-6 py-16 lg:grid-cols-[1.3fr_1fr]">
        <form data-upbo-src="src/pages/Checkout.tsx:80" onSubmit={submit} className="space-y-4">
          <h1 data-upbo-src="src/pages/Checkout.tsx:81" className="text-4xl font-semibold tracking-[-0.02em] text-[#5f3f38]">Thanh toán</h1>
          <p data-upbo-src="src/pages/Checkout.tsx:82" className="pb-2 text-[#9c7a72]">Điền thông tin nhận hàng, LumiSkin gọi xác nhận trước khi giao.</p>

          <input data-upbo-src="src/pages/Checkout.tsx:84" required value={form.customerName} onChange={set('customerName')} placeholder="Họ và tên" className={field} />
          <input data-upbo-src="src/pages/Checkout.tsx:85" required type="email" value={form.customerEmail} onChange={set('customerEmail')} placeholder="Email" className={field} />
          <input data-upbo-src="src/pages/Checkout.tsx:86" required value={form.customerPhone} onChange={set('customerPhone')} placeholder="Số điện thoại" className={field} />
          <textarea data-upbo-src="src/pages/Checkout.tsx:87" required rows={3} value={form.shippingAddress} onChange={set('shippingAddress')} placeholder="Địa chỉ nhận hàng" className={field} />
          <textarea data-upbo-src="src/pages/Checkout.tsx:88" rows={2} value={form.note} onChange={set('note')} placeholder="Ghi chú (không bắt buộc)" className={field} />

          <button data-upbo-src="src/pages/Checkout.tsx:90"
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-[#7a4f45] py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#63403a] disabled:opacity-60"
          >
            {submitting ? 'Đang gửi đơn...' : 'Đặt hàng'}
          </button>
        </form>

        <aside data-upbo-src="src/pages/Checkout.tsx:99" className="h-fit rounded-2xl border border-[#eedfda] bg-[#fdf8f6] p-6">
          <h2 data-upbo-src="src/pages/Checkout.tsx:100" className="text-sm font-medium uppercase tracking-[0.18em] text-[#b3897f]">Đơn hàng</h2>

          <ul data-upbo-src="src/pages/Checkout.tsx:102" className="mt-5 space-y-3">
            {items.map((item) => (
              <li data-upbo-src="src/pages/Checkout.tsx:104" key={item.id} className="flex justify-between gap-4 text-sm">
                <span data-upbo-src="src/pages/Checkout.tsx:105" className="min-w-0 truncate text-[#5f3f38]">
                  {item.name} <span data-upbo-src="src/pages/Checkout.tsx:106" className="text-[#c0a49d]">× {item.quantity}</span>
                </span>
                <span data-upbo-src="src/pages/Checkout.tsx:108" className="shrink-0 text-[#7a4f45]">{formatPrice(item.price * item.quantity)}</span>
              </li>
            ))}
          </ul>

          <div data-upbo-src="src/pages/Checkout.tsx:113" className="mt-5 flex items-center justify-between border-t border-[#eedfda] pt-5">
            <span data-upbo-src="src/pages/Checkout.tsx:114" className="text-sm text-[#9c7a72]">Tổng cộng</span>
            <span data-upbo-src="src/pages/Checkout.tsx:115" className="text-xl font-semibold text-[#7a4f45]">{formatPrice(total)}</span>
          </div>
        </aside>
      </div>
    </>
  )
}
